import type { ReactNode } from 'react';
import type { Estado } from './api';

/**
 * Las primitivas. Todo lo demás se compone de aquí.
 *
 * Nada de hexadecimales: solo variables. Un color escrito a mano en un JSX es un color que
 * no cambia en modo oscuro, y nadie lo nota hasta que un cliente lo reporta.
 */

// ── Veredicto ────────────────────────────────────────────────────────────────────

const VEREDICTO: Record<Estado, { texto: string; fg: string; bg: string }> = {
  aprobado: { texto: 'Aprobado', fg: 'var(--ok)', bg: 'var(--ok-weak)' },
  revision: { texto: 'A revisión', fg: 'var(--warn)', bg: 'var(--warn-weak)' },
  rechazado: { texto: 'Rechazado', fg: 'var(--bad)', bg: 'var(--bad-weak)' },
};

/**
 * La pastilla de veredicto. Es lo único que el cliente ve de una decisión.
 *
 * Sin iconos de alarma y sin rojo agresivo en "rechazado": el rojo aquí es sobrio a
 * propósito. Detrás de cada pastilla hay una persona, y la UI no debería celebrar.
 */
export function Pastilla({ estado }: { estado: Estado }) {
  const v = VEREDICTO[estado];
  return (
    <span
      className="inline-flex items-center rounded-md px-2.5 py-[3px] text-[12px] font-semibold"
      style={{ color: v.fg, background: v.bg }}
    >
      {v.texto}
    </span>
  );
}

/** Iniciales. Nunca una foto: la foto de una persona no decora una tabla. */
export function Avatar({ nombre, tono = 'accent' }: { nombre: string; tono?: 'accent' | 'ink' }) {
  const iniciales = nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full text-[11px] font-bold"
      style={
        tono === 'accent'
          ? { background: 'var(--accent-weak)', color: 'var(--accent)' }
          : { background: 'rgba(255,255,255,.08)', color: 'var(--ink-text-strong)' }
      }
      aria-hidden
    >
      {iniciales}
    </span>
  );
}

/**
 * Una métrica del resumen.
 *
 * `sub` no es decoración: traduce el número a la unidad que el cliente piensa. Nadie sabe
 * qué son S/ 12.480 en su operación; todos entienden "≈ 3.300 verificaciones".
 */
export function Metrica({
  label,
  valor,
  sub,
  tono,
}: {
  label: string;
  valor: string;
  // El `| undefined` explícito es por exactOptionalPropertyTypes: sin él no se puede pasar
  // `sub={cond ? texto : undefined}`, que es justamente el caso en el que no hay dato que
  // mostrar. Es como @types/react declara sus propias props opcionales.
  sub?: string | undefined;
  tono?: 'warn' | undefined;
}) {
  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--surface-2)' }}>
      <div className="text-[13px]" style={{ color: 'var(--text-2)' }}>
        {label}
      </div>
      <div
        className="tabular mt-0.5 text-[24px] font-semibold leading-tight"
        style={tono === 'warn' ? { color: 'var(--warn)' } : undefined}
      >
        {valor}
      </div>
      {sub ? (
        <div className="mt-0.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export function Tarjeta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl ${className}`}
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
    >
      {children}
    </div>
  );
}

export function Boton({
  children,
  onClick,
  variante = 'normal',
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variante?: 'normal' | 'primario';
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const primario = variante === 'primario';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg px-3.5 py-2 text-[13px] font-semibold transition disabled:opacity-50"
      style={
        primario
          ? { background: 'var(--accent)', color: 'var(--accent-fg)' }
          : {
              background: 'var(--surface)',
              color: 'var(--text-2)',
              border: '1px solid var(--border-2)',
            }
      }
    >
      {children}
    </button>
  );
}

/**
 * El estado vacío.
 *
 * No es un cartel de "sin datos": es la primera impresión del producto y tiene que ser un
 * camino. Un cliente que entra y ve "No hay resultados" no aprendió nada; uno que ve
 * "Verifica a tu primera persona" ya sabe qué hacer.
 */
export function Vacio({
  titulo,
  texto,
  accion,
}: {
  titulo: string;
  texto: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <div className="text-[15px] font-semibold">{titulo}</div>
      <div className="max-w-[420px] text-[13px]" style={{ color: 'var(--text-2)' }}>
        {texto}
      </div>
      {accion ? <div className="mt-3">{accion}</div> : null}
    </div>
  );
}

/**
 * El aviso de que la fuente falló.
 *
 * `reembolsado` cambia el mensaje, y no es un detalle cosmético: un error que le costó plata
 * al cliente y uno que no son cosas distintas. Si no decimos que le devolvimos el dinero,
 * nadie lo comprueba — y una factura que no se puede auditar es una factura en la que se
 * deja de confiar.
 */
export function AvisoError({ mensaje, reembolsado }: { mensaje: string; reembolsado: boolean }) {
  return (
    <div
      className="rounded-lg px-4 py-3 text-[13px]"
      style={{ background: 'var(--bad-weak)', color: 'var(--bad)' }}
      role="alert"
    >
      {mensaje}
      {reembolsado ? (
        <div className="mt-1 font-semibold">No se te cobró esta consulta.</div>
      ) : null}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ background: 'var(--border)' }}
      aria-hidden
    />
  );
}
